import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

type SizeGuideRow = Record<string, string | number | undefined>;
type SizeGuideTableProps = {
  data: SizeGuideRow[];
};

const prettify = (key: string) =>
  key
    .replace('_', ' ')
    .replace(/\b\w/g, c => c.toUpperCase()); // "abertura_punho" -> "Abertura Punho"

const SizeGuideTable = ({ data }: SizeGuideTableProps) => {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }

  const sizeKeyCandidates = ['medida', 'colarinho'];
  const sizeKey =
    (sizeKeyCandidates.find(k => data.some(r => (r[k] ?? '') !== '')) as string) ||
    'medida';

  const rawSizes = Array.from(
    new Set(
      data
        .map(r => String(r[sizeKey] ?? '').trim())
        .filter(v => v !== '')
    )
  );

  const sizes = rawSizes
    .slice()
    .sort((a, b) => {
      const na = Number(String(a).replace(',', '.'));
      const nb = Number(String(b).replace(',', '.'));
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });

  const bySize: Record<string, SizeGuideRow> = {};
  data.forEach(r => {
    const key = String(r[sizeKey] ?? '').trim();
    if (key) bySize[key] = r;
  });

  const allKeys = Array.from(
    new Set(data.flatMap(r => Object.keys(r)))
  ).filter(k => k !== sizeKey);

  const measureKeyForSort = allKeys[1] ?? null;

  const toNumber = (value: unknown): number | null => {
    const raw = String(value ?? '').trim();
    if (raw === '') return null;
    const normalized = raw.replace(',', '.');
    const match = normalized.match(/-?\d+(?:\.\d+)?/);
    if (!match) return null;
    const n = Number(match[0]);
    return Number.isFinite(n) ? n : null;
  };

  const sortedSizes = measureKeyForSort
    ? sizes.slice().sort((a, b) => {
        const va = bySize[a]?.[measureKeyForSort];
        const vb = bySize[b]?.[measureKeyForSort];
        const na = toNumber(va);
        const nb = toNumber(vb);
        if (na !== null && nb !== null) return na - nb;
        if (na !== null) return -1;
        if (nb !== null) return 1;
        return String(va ?? '').localeCompare(String(vb ?? ''));
      })
    : sizes;


  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 170 }}>Medidas</TableCell>
            {sortedSizes.map(size => (
              <TableCell key={`hdr-${size}`}>{size}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {allKeys.map((measureKey) => (
            <TableRow key={`row-${measureKey}`}>
              <TableCell sx={{ fontWeight: 600 }}>
                {prettify(measureKey)}
              </TableCell>
              {sortedSizes.map(size => {
                const val = bySize[size]?.[measureKey];
                return (
                  <TableCell key={`cell-${measureKey}-${size}`}>
                    {val ?? '—'}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SizeGuideTable;
